import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import './About.scss';

export interface CompanyInfo {
  address: string;
  phone: string;
  kakaoChannel: string;
  email: string;
}

interface CoreValue {
  id: string;
  title: string;
  titleKo: string;
  description: string;
}

interface TeamRole {
  id: string;
  label: string;
  description: string;
}

const COMPANY_INFO: CompanyInfo = {
  address: '대구광역시 달성군 다사읍 달구벌대로174길50, B202호',
  phone: '1551-5948',
  kakaoChannel: 'http://pf.kakao.com/_xmpdXX',
  email: 'yeplat@naver.com',
};

const CORE_VALUES: CoreValue[] = [
  {
    id: 'customer-centric',
    title: 'Customer Centric',
    titleKo: '고객 중심',
    description: '비즈니스 목표를 깊이 이해하고, 고객의 관점에서 함께 고민하며 최적의 솔루션을 제안합니다.',
  },
  {
    id: 'technical-excellence',
    title: 'Technical Excellence',
    titleKo: '기술적 탁월함',
    description: '확장 가능하고 안정적인 아키텍처를 설계하여, 기술 부채 없이 성장할 수 있는 기반을 만듭니다.',
  },
  {
    id: 'sustainable-growth',
    title: 'Sustainable Growth',
    titleKo: '지속 가능한 성장',
    description: '단순 출시를 넘어 데이터 기반의 운영과 고도화를 통해 프로덕트의 지속 가능한 성장을 지원합니다.',
  },
];

const TEAM_ROLES: TeamRole[] = [
  {
    id: 'planning',
    label: '기획',
    description: '비즈니스 목표 정의 · 요구사항 설계 · 로드맵 수립',
  },
  {
    id: 'design',
    label: 'UI/UX 디자인',
    description: '사용자 리서치 · 인터페이스 설계 · 디자인 시스템',
  },
  {
    id: 'development',
    label: '풀스택 개발',
    description: '프론트엔드 · 백엔드 · 인프라 · DevOps',
  },
];

const About: React.FC = () => {
  const mapElement = useRef<HTMLDivElement | null>(null);
  const { hash } = useLocation();

  useEffect(() => {
    try {
      const { naver } = window;
      const location = new naver.maps.LatLng(35.85815, 128.46005);
      const mapEl = mapElement.current;
      if (!mapEl) return;

      const map = new naver.maps.Map(mapEl, {
        center: location,
        zoom: 13,
        zoomControl: true,
      });
      new naver.maps.Marker({ position: location, map });
    } catch (error) {
      console.error('지도 로드 오류:', error);
    }
  }, []);

  // /about#location 등 해시로 진입 시 해당 섹션으로 스크롤
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <div className="about">
      <Header />

      <section className="about-hero">
        <div className="container about-hero__inner">
          <span className="about-hero__eyebrow">About YEPLAT</span>
          <h1 className="about-hero__title">
            우리는 기술로
            <br />
            비즈니스의 가능성을 확장합니다.
          </h1>
          <p className="about-hero__desc">
            예플랫은 앱과 웹 플랫폼 개발을 통해 고객의 디지털 혁신을 실현하는 기술 파트너입니다.
            기획부터 설계, 개발, 운영까지 하나의 팀으로 밀착 협업하며 비즈니스 성장의
            든든한 동반자가 되겠습니다.
          </p>
        </div>
      </section>

      <section className="about-section about-values" id="values">
        <div className="container">
          <header className="section-header">
            <span className="section-header__label">Core Values</span>
            <h2 className="section-header__title">예플랫이 지키는 3가지 핵심 가치</h2>
            <p className="section-header__desc">
              모든 프로젝트의 시작과 끝에, 우리는 이 가치를 기준으로 판단하고 실행합니다.
            </p>
          </header>

          <div className="values-grid">
            {CORE_VALUES.map((value, index) => (
              <article key={value.id} className="value-card">
                <div className="value-card__index">{index + 1}</div>
                <h3 className="value-card__title">{value.title}</h3>
                <span className="value-card__title-ko">{value.titleKo}</span>
                <p className="value-card__desc">{value.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-culture">
        <div className="container">
          <header className="section-header section-header--light">
            <span className="section-header__label">Organization & Culture</span>
            <h2 className="section-header__title">
              기획 · 디자인 · 개발이 하나로,
              <br />
              올라운더 그룹이 만듭니다.
            </h2>
            <p className="section-header__desc">
              예플랫은 각 분야의 전문가들이 유기적으로 협업하는 올라운더 그룹입니다.
              단일 팀 체제로 커뮤니케이션 비용을 줄이고, 빠른 의사결정과 높은 완성도를 추구합니다.
            </p>
          </header>

          <div className="culture-visual">
            <div className="culture-hub">
              <span className="culture-hub__label">YEPLAT</span>
              <span className="culture-hub__sub">All-Rounder Team</span>
            </div>

            <div className="culture-roles">
              {TEAM_ROLES.map((role) => (
                <article key={role.id} className={`culture-role culture-role--${role.id}`}>
                  <div className="culture-role__icon" aria-hidden="true" />
                  <h3 className="culture-role__label">{role.label}</h3>
                  <p className="culture-role__desc">{role.description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="about-section about-location" id="location">
        <div className="container">
          <header className="section-header">
            <span className="section-header__label">Office Location</span>
            <h2 className="section-header__title">오시는 길</h2>
            <p className="section-header__desc">
              예플랫을 방문하시거나 프로젝트 상담을 원하시면 아래 연락처로 문의해 주세요.
            </p>
          </header>

          <div className="location-grid">
            <div className="location-info">
              <div className="info-block">
                <span className="info-block__label">Address</span>
                <p className="info-block__value">{COMPANY_INFO.address}</p>
              </div>
              <div className="info-block">
                <span className="info-block__label">Tel</span>
                <a className="info-block__value info-block__link" href={`tel:${COMPANY_INFO.phone}`}>
                  {COMPANY_INFO.phone}
                </a>
              </div>
              <div className="info-block">
                <span className="info-block__label">Kakao Channel</span>
                <a
                  className="info-block__value info-block__link"
                  href={COMPANY_INFO.kakaoChannel}
                  target="_blank"
                  rel="noreferrer"
                >
                  {COMPANY_INFO.kakaoChannel}
                </a>
              </div>
              <div className="info-block">
                <span className="info-block__label">Email</span>
                <a className="info-block__value info-block__link" href={`mailto:${COMPANY_INFO.email}`}>
                  {COMPANY_INFO.email}
                </a>
              </div>
            </div>

            <div className="location-map">
              <div id="map" ref={mapElement} style={{ minHeight: '400px' }} />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
